import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLoginContext } from './LoginContext.jsx';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { AnimatePresence, motion } from 'framer-motion';
import {
    Search, Menu, X, User, LogOut, Settings, Calendar,
    DollarSign, FileText, HelpCircle, BookOpen, Plus,
    Bell, ChevronDown, Home,
    Shield, Grid3X3, Tag, Sparkles,
    Info, Phone, History
} from 'lucide-react';
import notify from './Nots.jsx';

const Header = () => {
    const { postRequest, getRequest, putRequest } = useApiRequest();
    const { isLoggedIn, setIsLoggedIn, userInfo, setUserInfo, handleLogout } = useLoginContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [isSticky, setIsSticky] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [openSections, setOpenSections] = useState({
        main: true,
        more: false,
        categories: false,
    });

    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isUserDropdownOpen && !event.target.closest('.user-dropdown')) {
                setIsUserDropdownOpen(false);
            }
            if (isNotificationsOpen && !event.target.closest('.notifications-dropdown')) {
                setIsNotificationsOpen(false);
            }
            if (isCategoriesOpen && !event.target.closest('.categories-dropdown')) {
                setIsCategoriesOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isUserDropdownOpen, isNotificationsOpen, isCategoriesOpen]);

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await getRequest(`${domain}/event-categories`);
                if (response.status === 200) {
                    console.log('Categories from API:', response.body);
                    setCategories(response.body);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Fetch notifications
    const fetchNotifications = async () => {
        if (!userInfo?.accessToken) return;

        try {
            setNotificationsLoading(true);
            const response = await getRequest(`${domain}/notifications`);
            if (response.status === 200) {
                setNotifications(response.body.slice(0, 5)); // Show only latest 5
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setNotificationsLoading(false);
        }
    };

    // Fetch unread count
    const fetchUnreadCount = async () => {
        if (!userInfo?.accessToken) return;

        try {
            const response = await getRequest(`${domain}/notifications/unread-count`);
            if (response.status === 200) {
                setUnreadCount(response.body.unread_count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    // Fetch notifications when user is logged in
    useEffect(() => {
        if (userInfo?.accessToken) {
            fetchNotifications();
            fetchUnreadCount();
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [userInfo?.accessToken]);

    // Refresh notifications periodically
    useEffect(() => {
        if (userInfo?.accessToken) {
            const interval = setInterval(() => {
                fetchUnreadCount();
            }, 30000); // Check every 30 seconds

            return () => clearInterval(interval);
        }
    }, [userInfo?.accessToken]);

    // Mark notification as read
    const markNotificationAsRead = async (notificationId) => {
        try {
            const response = await putRequest(`${domain}/notifications/${notificationId}/mark-read`);
            if (response.status === 200) {
                // Update local state
                setNotifications(prev =>
                    prev.map(notif =>
                        notif.id === notificationId
                            ? { ...notif, is_read: true }
                            : notif
                    )
                );
                // Update unread count
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all notifications as read
    const markAllNotificationsAsRead = async () => {
        try {
            const response = await putRequest(`${domain}/notifications/mark-all-read`);
            if (response.status === 200) {
                setNotifications(prev =>
                    prev.map(notif => ({ ...notif, is_read: true }))
                );
                setUnreadCount(0);
                notify('All notifications marked as read', 'success');
            }
        } catch (error) {
            notify('Failed to mark all notifications as read', 'error');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setIsSearchOpen(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsSticky(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // Close mobile menu when route changes
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
        setIsUserDropdownOpen(false);
        setIsNotificationsOpen(false);
        setIsCategoriesOpen(false);
    }, [location.pathname]);

    // Main navigation items (reduced)
    const mainNavigationItems = [
        { name: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
        { name: 'Events', href: '/events', icon: <Calendar className="w-4 h-4" /> }
    ];

    // Secondary navigation items
    const secondaryNavigationItems = [
        { name: 'How it Works', href: '/how-it-works', icon: <BookOpen className="w-4 h-4" /> },
        { name: 'Help Center', href: '/help-center', icon: <HelpCircle className="w-4 h-4" /> },
        { name: 'Passed Events', href: '/passed-events', icon: <History className="w-4 h-4" /> },
        { name: 'About Us', href: '/about', icon: <Info className="w-4 h-4" /> },
        { name: 'Contact', href: '/contact', icon: <Phone className="w-4 h-4" /> }
    ];

    const userMenuItems = [
        { name: 'My Profile', href: '/accounts/profile', icon: <User className="w-4 h-4" /> },
        { name: 'My Events', href: '/my-events', icon: <Calendar className="w-4 h-4" /> },
        { name: 'My Revenue', href: '/my-revenue', icon: <DollarSign className="w-4 h-4" /> },
        { name: 'Payment Requests', href: '/my-payment-requests', icon: <FileText className="w-4 h-4" /> },
        { name: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> }
    ];

    const popularCategories = [
        { name: 'Music & Concerts', color: 'from-purple-500 to-pink-500', icon: '🎵', slug: 'music-concerts' },
        { name: 'Sports & Fitness', color: 'from-green-500 to-emerald-500', icon: '⚽', slug: 'sports-fitness' },
        { name: 'Business & Tech', color: 'from-blue-500 to-cyan-500', icon: '💼', slug: 'business-tech' },
        { name: 'Food & Drink', color: 'from-orange-500 to-red-500', icon: '🍕', slug: 'food-drink' },
        { name: 'Arts & Culture', color: 'from-pink-500 to-rose-500', icon: '🎨', slug: 'arts-culture' },
        { name: 'Education', color: 'from-indigo-500 to-purple-500', icon: '📚', slug: 'education' }
    ];

    const SectionHeader = ({ title, sectionKey, icon = null }) => (
        <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            className="w-full flex items-center justify-between px-4 py-2 rounded-xl hover:bg-gray-50 transition"
        >
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {title}
                </span>
            </div>

            <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${openSections[sectionKey] ? "rotate-0" : "-rotate-90"
                    }`}
            />
        </button>
    );

    return (
        <header className={`w-full transition-all duration-500 ${isSticky
            ? 'bg-white shadow-lg border-b border-gray-200'
            : 'bg-white'
            }`}>
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-orange-500 to-teal-500"></div>

            <div className="w-full">
                {/* Main Header Row */}
                <div className="flex items-center justify-between h-16 px-4">
                    {/* Logo */}
                    <Link to="/" id='link' className="flex items-center gap-3 group">
                        <div className="relative">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                                <img src="/images/logo.png" alt="EventPlots Logo" className="w-10 h-10 rounded-lg" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xl font-bold transition-all duration-300">
                                <span className="text-orange-500 group-hover:text-orange-600">Event</span>
                                <span className="text-teal-600 group-hover:text-teal-700">Plots</span>
                            </span>
                            <span className="text-xs text-gray-500 -mt-1">Discover Amazing Events</span>
                        </div>
                    </Link>

                    {/* Main Navigation (Reduced) */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {mainNavigationItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${location.pathname === item.href
                                    ? 'text-teal-600 bg-teal-50 shadow-sm'
                                    : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                                    }`}
                            >
                                {item.icon}
                                {item.name}
                            </Link>
                        ))}

                        {/* Categories Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${isCategoriesOpen
                                    ? 'text-teal-600 bg-teal-50 shadow-sm'
                                    : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                                    }`}
                            >
                                <Grid3X3 className="w-4 h-4" />
                                Categories
                                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCategoriesOpen ? 'rotate-180' : ''
                                    }`} />
                            </button>

                            <AnimatePresence>
                                {isCategoriesOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                        className="absolute top-full left-0 mt-3 w-96 bg-white rounded-xl shadow-xl border border-gray-200 p-6 z-50"
                                    >
                                        {/* Popular Categories */}
                                        <div className="mb-6">
                                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                                                <Sparkles className="w-4 h-4 text-orange-500" />
                                                Popular Categories
                                            </h3>
                                            <div className="grid grid-cols-2 gap-3">
                                                {categoriesLoading ? (
                                                    <div className="col-span-2 space-y-3">
                                                        {[...Array(6)].map((_, i) => (
                                                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-100 animate-pulse">
                                                                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                                                <div className="flex-1">
                                                                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                                                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : categories.length > 0 ? (
                                                    categories
                                                        .filter(category => category.name && category.name.trim() !== '')
                                                        .slice(0, 6)
                                                        .map((category, index) => {
                                                            const popularCategory = popularCategories.find(pc =>
                                                                pc.name.toLowerCase().includes(category.name.toLowerCase()) ||
                                                                category.name.toLowerCase().includes(pc.name.toLowerCase())
                                                            );

                                                            return (
                                                                <Link
                                                                    key={category.id}
                                                                    to={`/categories/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                                    className="group relative overflow-hidden rounded-lg p-3 hover:shadow-md transition-all duration-300 hover:scale-105"
                                                                >
                                                                    <div className={`absolute inset-0 bg-gradient-to-br ${popularCategory?.color || 'from-gray-500 to-gray-600'} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
                                                                    <div className="relative flex items-center gap-3">
                                                                        <div className={`w-10 h-10 bg-gradient-to-br ${popularCategory?.color || 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center !text-white text-lg shadow-sm group-hover:shadow-md transition-all duration-300`}>
                                                                            {popularCategory?.icon || '📅'}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors duration-200">
                                                                                {category.name || 'Unnamed Category'}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {category.event_count} event{category.event_count !== 1 ? 's' : ''}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </Link>
                                                            );
                                                        })
                                                ) : (
                                                    <div className="col-span-2 text-center py-4">
                                                        <p className="text-sm text-gray-500">No categories available</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* All Categories */}
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                                                <Tag className="w-4 h-4 text-teal-500" />
                                                All Categories
                                            </h3>
                                            <div className="max-h-48 overflow-y-auto space-y-2 pr-2">
                                                {categoriesLoading ? (
                                                    <div className="space-y-3">
                                                        {[...Array(6)].map((_, i) => (
                                                            <div key={i} className="flex items-center gap-3">
                                                                <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                                                                <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : categories.length > 0 ? (
                                                    categories
                                                        .filter(category => category.name && category.name.trim() !== '')
                                                        .map((category, index) => (
                                                            <Link
                                                                key={index}
                                                                to={`/categories/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                                className="flex items-center justify-between p-2 rounded-lg hover:bg-teal-50 transition-all duration-200 group"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-teal-500 rounded-full group-hover:scale-150 transition-transform duration-200"></div>
                                                                    <span className="text-sm text-gray-700 group-hover:text-teal-600 font-medium transition-colors duration-200">
                                                                        {category.name || 'Unnamed Category'}
                                                                    </span>
                                                                </div>
                                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                                    {category.event_count} event{category.event_count !== 1 ? 's' : ''}
                                                                </span>
                                                            </Link>
                                                        ))
                                                ) : (
                                                    <p className="text-sm text-gray-500 text-center py-4">
                                                        No categories available
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* View All Categories Link */}
                                        <div className="mt-6 pt-4 border-t border-gray-100">
                                            <Link
                                                to="/categories"
                                                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                            >
                                                View All Categories
                                                <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                                            </Link>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </nav>

                    {/* Search Bar */}
                    <div className="hidden md:flex flex-1 max-w-lg mx-8 min-w-0">
                        <form onSubmit={handleSearch} className="relative w-full">
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-teal-500 transition-colors duration-200 pointer-events-none" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search events, locations, or categories..."
                                    className="w-full pl-12 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md resize-none overflow-hidden"
                                    style={{ minWidth: '200px', maxWidth: '100%' }}
                                />
                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 z-10"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-2">
                        {/* Notifications */}
                        <div className="relative notifications-dropdown">
                            <button
                                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                                className="relative p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                            >
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs !text-white flex items-center justify-center font-bold animate-pulse">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>

                            <AnimatePresence>
                                {isNotificationsOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                        className="fixed sm:absolute right-4 sm:right-0 top-20 sm:top-full mt-2 w-72 sm:w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 p-3 sm:p-4 z-50 notifications-dropdown"
                                        style={{
                                            maxWidth: 'calc(100vw - 2rem)',
                                            minWidth: '280px'
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                                            <h3 className="text-sm sm:text-base font-bold text-gray-900">Notifications</h3>
                                            <button
                                                onClick={markAllNotificationsAsRead}
                                                className="text-xs sm:text-sm text-teal-600 hover:text-teal-700 font-medium"
                                            >
                                                Mark all read
                                            </button>
                                        </div>
                                        <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                                            {notificationsLoading ? (
                                                <div className="space-y-2 sm:space-y-3">
                                                    {[...Array(3)].map((_, i) => (
                                                        <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3">
                                                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                                                            <div className="flex-1 space-y-1 sm:space-y-2">
                                                                <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
                                                                <div className="h-2 bg-gray-200 rounded animate-pulse w-1/2"></div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : notifications.length > 0 ? (
                                                notifications.map((notification) => (
                                                    <div
                                                        key={notification.id}
                                                        className={`p-2 sm:p-3 rounded-lg transition-all duration-200 cursor-pointer hover:bg-gray-50 ${!notification.is_read ? 'bg-orange-50 border border-orange-100' : 'bg-gray-50'
                                                            }`}
                                                        onClick={() => markNotificationAsRead(notification.id)}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <p className="text-xs sm:text-sm font-medium text-gray-900">
                                                                    {notification.title}
                                                                </p>
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {notification.time_ago}
                                                                </p>
                                                            </div>
                                                            {!notification.is_read && (
                                                                <div className="w-2 h-2 bg-orange-500 rounded-full ml-2 flex-shrink-0"></div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-xs sm:text-sm text-gray-500 text-center py-3 sm:py-4">
                                                    No notifications
                                                </p>
                                            )}
                                        </div>
                                        {notifications.length > 0 && (
                                            <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-200">
                                                <Link
                                                    to="/notifications"
                                                    className="block text-center text-xs sm:text-sm text-teal-600 hover:text-teal-700 font-medium"
                                                >
                                                    View all notifications
                                                </Link>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Create Event Button */}
                        {userInfo?.accessToken && (
                            <Link
                                to="/create-plot"
                                className="hidden md:flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 !text-white rounded-lg font-semibold transition-all duration-300 hover:shadow-md hover:scale-105"
                            >
                                <Plus className="w-4 h-4" />
                                Create Event
                            </Link>
                        )}

                        {/* User Menu */}
                        {userInfo?.accessToken ? (
                            <div className="relative user-dropdown">
                                <button
                                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                    className="flex items-center gap-2 p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                >
                                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                                        <User className="w-4 h-4 !text-white" />
                                    </div>
                                    <span className="hidden lg:block text-sm font-semibold">
                                        {userInfo?.user?.owner?.first_name || 'User'}
                                    </span>
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                <AnimatePresence>
                                    {isUserDropdownOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 20, scale: 0.95 }}
                                            transition={{ duration: 0.3 }}
                                            className="absolute right-0 top-full mt-3 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50"
                                        >
                                            {/* User Info */}
                                            <div className="p-4 border-b border-gray-100 mb-3 bg-gradient-to-r from-orange-50 to-teal-50 rounded-lg">
                                                <p className="font-bold text-gray-900">
                                                    {userInfo?.user?.owner?.first_name} {userInfo?.user?.owner?.last_name}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {userInfo?.user?.owner?.email}
                                                </p>
                                            </div>

                                            {/* Menu Items */}
                                            <div className="space-y-1">

                                                {userInfo?.user?.owner?.is_staff && userInfo?.user?.owner?.is_superuser && (
                                                    <Link
                                                        to="/admin"
                                                        className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 ${location.pathname === '/admin'
                                                            ? 'bg-orange-50 text-orange-600 shadow-sm'
                                                            : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                                                            }`}
                                                    >
                                                        <Shield className="w-4 h-4" />
                                                        Admin Panel
                                                    </Link>
                                                )}

                                                {userMenuItems.map((item) => (
                                                    <Link
                                                        key={item.name}
                                                        to={item.href}
                                                        onClick={() => setIsUserDropdownOpen(false)}
                                                        className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer ${location.pathname === item.href
                                                            ? 'bg-orange-50 text-orange-600 shadow-sm'
                                                            : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                                                            }`}
                                                    >
                                                        {item.icon}
                                                        {item.name}
                                                    </Link>
                                                ))}

                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center gap-3 px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                                >
                                                    <LogOut className="w-4 h-4" />
                                                    Sign Out
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link
                                    to="/login"
                                    className="relative p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                >
                                    <User className="w-5 h-5" />
                                </Link>
                                <Link
                                    to="/register"
                                    className="hidden sm:flex items-center gap-2 px-4 py-2 !bg-teal-600 hover:!bg-teal-700 !text-white rounded-lg font-semibold transition-all duration-200 hover:shadow-md hover:scale-105"
                                >
                                    Sign Up
                                </Link>
                            </div>
                        )}

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                        >
                            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Secondary Navigation Row */}
                <div className="hidden lg:flex items-center justify-center h-12 bg-gray-800 border-t border-gray-700">
                    <nav className="flex items-center gap-8" id='nav'>
                        {secondaryNavigationItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${location.pathname === item.href
                                    ? '!text-white bg-orange-500 shadow-md'
                                    : '!text-gray-300 hover:text-white hover:bg-gray-700'
                                    }`}
                            >
                                {item.icon}
                                {item.name}
                            </Link>
                        ))}
                    </nav>
                </div>

                {/* Mobile Search Bar */}
                <AnimatePresence>
                    {isSearchOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="md:hidden py-4 border-t border-gray-100"
                        >
                            <form onSubmit={handleSearch} className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search events, locations..."
                                    className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gray-50 focus:bg-white resize-none overflow-hidden"
                                    style={{ minWidth: '200px', maxWidth: '100%' }}
                                />
                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 z-10"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden bg-white border-t border-gray-100 shadow-2xl"
                    >
                        <div className="container mx-auto px-4 py-0 my-0!">
                            {/* Mobile Actions */}
                            <div className="space-y-0">
                                {!userInfo?.accessToken ? (
                                    <div className='flex items-center justify-center gap-2'>
                                        <Link
                                            to="/login"
                                            className="text-sm! flex items-center justify-center gap-2 w-full px-4 mt-2 py-2 border border-gray-60 text-gray-600! hover:bg-blue-50 rounded-xl font-medium transition-all duration-200"
                                        >
                                            Sign In
                                        </Link>
                                        <Link
                                            to="/register"
                                            className="text-sm! flex items-center justify-center gap-2 w-full px-4 mt-2 py-2 border border-gray-60 text-gray-600! rounded-xl font-semibold transition-all duration-200"
                                        >
                                            Sign Up
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <Link
                                            to="/create-plot"
                                            className="flex items-center justify-center gap-2 w-full px-4 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 !text-white rounded-xl font-semibold transition-all duration-200"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Create Event
                                        </Link>
                                        <div className="space-y-2">
                                            {userMenuItems.map((item) => (
                                                <Link
                                                    key={item.name}
                                                    to={item.href}
                                                    onClick={() => console.log('Mobile navigating to:', item.href)}
                                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${location.pathname === item.href
                                                        ? 'bg-orange-50 text-orange-600 shadow-md'
                                                        : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                                                        }`}
                                                >
                                                    {item.icon}
                                                    {item.name}
                                                </Link>
                                            ))}
                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Sign Out
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Mobile Navigation */}
                            <nav className="space-y-2 mb-6">
                                {/* Main Navigation */}
                                <div className="mb-4">
                                    <SectionHeader title="Main" sectionKey="main" />
                                    {openSections.main &&
                                        mainNavigationItems.map((item) => (
                                            <Link
                                                key={item.name}
                                                to={item.href}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${location.pathname === item.href
                                                    ? 'text-teal-600 bg-teal-50 shadow-md'
                                                    : 'text-gray-600 hover:text-teal-600 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {item.icon}
                                                {item.name}
                                            </Link>
                                        ))}
                                </div>

                                {/* Secondary Navigation */}
                                <div className="mb-4">
                                    <SectionHeader title="Learn More" sectionKey="more" />

                                    {openSections.more &&
                                        secondaryNavigationItems.map((item) => (
                                            <Link
                                                key={item.name}
                                                to={item.href}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${location.pathname === item.href
                                                    ? 'text-orange-600 bg-orange-50 shadow-md'
                                                    : 'text-gray-600 hover:text-orange-600 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {item.icon}
                                                {item.name}
                                            </Link>
                                        ))}
                                </div>

                                {/* Mobile Categories */}
                                <div className="border-t border-gray-100 pt-2">
                                    <div className='pb-3'>
                                        <button
                                            type="button"
                                            onClick={() => toggleSection("categories")}
                                            className="w-full flex items-center justify-between px-4 py-2 rounded-xl hover:bg-gray-50 transition "
                                        >
                                            <div className="flex items-center gap-2">
                                                <Grid3X3 className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm font-bold text-gray-400">Categories</span>
                                            </div>

                                            <ChevronDown
                                                className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${openSections.categories ? "rotate-0" : "-rotate-90"
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                    {openSections.categories && (
                                        <div className="space-y-2">
                                            {categoriesLoading ? (
                                                <div className="space-y-3">
                                                    {[...Array(6)].map((_, i) => (
                                                        <div key={i} className="flex items-center gap-3">
                                                            <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                                                            <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : categories.length > 0 ? (
                                                categories.slice(0, 4).map((category, index) => (
                                                    <Link
                                                        key={index}
                                                        to={`/categories/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                        className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                                                    >
                                                        <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                                                        <span className="text-sm font-medium">{category.name}</span>
                                                    </Link>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-500 text-center py-4">
                                                    No categories available
                                                </p>
                                            )}
                                            {categories.length > 4 && (
                                                <Link
                                                    to="/categories"
                                                    className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                                                >
                                                    View All Categories
                                                    <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </nav>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Backdrop for dropdowns */}
            {(isUserDropdownOpen || isNotificationsOpen || isCategoriesOpen) && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => {
                        setIsUserDropdownOpen(false);
                        setIsNotificationsOpen(false);
                        setIsCategoriesOpen(false);
                    }}
                />
            )}
        </header>
    );
};

export default Header;